<template>
  <div>
    <!-- Search result: Facets -->
    <div
      v-if="primaryList && primaryList.facets"
      class="layout-searchpage__filters"
      :class="{
        'is-active': productFilterStore.showFacets,
      }"
    >
      <template v-if="searchpage">
        <h2
          v-if="
            primaryList &&
            primaryList.productGroups &&
            primaryList.productGroups.length > 0
          "
          class="layout-searchpage__subheading"
        >
          {{ $t("hitsIn") }} {{ $t("products") }}
          <span class="layout-searchpage__hit-count"
            >({{ primaryList.totalHits }} {{ $t("hits") }})</span
          >
        </h2>
      </template>
      <template v-else>
        <component
          :is="headingLevelWithDefault"
          class="layout-category__heading"
        >
          {{ heading }}
          <span
            v-if="
              primaryList &&
              primaryList.productGroups &&
              primaryList.productGroups.length > 0
            "
            class="u-hide-for-small layout-category__heading__count"
          >
            {{ primaryList.totalHits }} {{ $t("styles") }}
          </span>
        </component>
      </template>
      <!-- SEO Information -->
      <div v-if="props.seoInformation" class="layout-category__seo-information">
        <span>
          {{
            isSmallScreen && !isExpanded ? truncatedText : props.seoInformation
          }}
        </span>
        <button
          v-if="isSmallScreen && props.seoInformation.length > maxCharacters"
          @click="toggleReadMore"
          class="btn--read-more"
        >
          {{ isExpanded ? $t("readLess") : $t("readMore") }}
        </button>
      </div>
      <!-- Subcategories -->
      <template v-if="subcategories && subcategories.length != 0">
        <div class="product-subcategories">
          <template v-for="subcategory in subcategories" :key="subcategory.url">
            <a
              :href="subcategory.url"
              :class="{
                'is-active': subcategory.isSelected,
              }"
              class="product-subcategories__subcategory"
              >{{ subcategory.name }}</a
            >
          </template>
        </div>
      </template>
      <!-- End Subcategories -->
      <div class="product-sort__container">
        <div class="product-sort__count-container u-hide-for-medium">
        <span
            v-if="
              primaryList &&
              primaryList.productGroups &&
              primaryList.productGroups.length > 0
            "
            class="layout-category__heading__count"
          >
            {{ primaryList.totalHits }} produkter
        </span>
        </div>
      <div class="layout-searchpage__mobile-filter-container">
        <!-- Sortering Button -->
        <div class="product-sort">
          <div class="product-sort__dropdown">
            <button class="product-sort__button" @click="toggleSortDropdown">
              <Icon class="product-sort__icon" :type="IconTypes.ProductSortBy" :size="IconSizes.Small" />
              <span class="u-text-smaller">{{selectedSortLabel}}</span>
            </button>

            <div v-if="isSortDropdownVisible" class="product-sort__options">
              <div
                v-for="option in primaryList.sort.options"
                :key="option.id"
                :class="['product-sort__option', { 'product-sort__option--selected': option.id === selectedSortValue }]"
                @click="selectSortOption(option.id, option.label)"
              >
                {{ option.label }}
              </div>
            </div>
          </div>
        </div>
        <!-- Filter Button -->
        <div class="layout-searchpage__mobile-filters">
          <button
            class="btn btn--category-filters"
            ref="filterButton"
            :aria-expanded="productFilterStore.showFacets.toString()"
            @click="productFilterStore.toggleShowFacets()"
            @keyup.esc="esc()"
          >
            <Icon :type="IconTypes.ProductFilter" :size="IconSizes.Small" />
            <span class="u-text-smaller">{{ $t("filter") }} {{ selectedFiltersCountText }}</span>
          </button>
        </div>
      </div>
    </div>
      <div
        class="layout-category__navigation"
        ref="filterPanel"
        :class="{
          'is-active': productFilterStore.showFacets,
        }"
      >
        <div class="layout-category__navigation__scroll">
          <div
            class="product-filter__submit product-filter__submit--fixed u-hide-for-medium"
          >
          <span class="product-filter__toptext">
            {{ $t("filter") }}
          </span>
            <button
              type="button"
              class="btn btn--small btn--link btn--close product-filter__close"
              @click="productFilterStore.toggleShowFacets()"
            >
              <Icon type="close" />
            </button>
          </div>
          <div class="product-filter__facet-wrapper">
            <Facets
            v-if="primaryList.facets"
            :facets="productFilterStore.filters"
            :sort="primaryList.sort"
            @upsertFacet="upsertFacet($event)"
            @updateSort="updateSort($event)"
            @useFilter="useFilter()"
            />
            <Pills
              v-if="primaryList.facets"
              :facets="productFilterStore.filters"
              @upsertFacet="upsertFacet($event)"
            />
          </div>
        </div>
        <!-- Use Filter button for mobile -->
        <div class="product-filter__facetsubmit u-hide-for-medium">
            <button
              class="btn btn--profile btn--small"
              @click="useFilter()"
            >
              Vis {{ productFilterStore.totalHits }} produkter
            </button>
          </div>
      </div>
    </div>
    <!-- Search result: Products -->
    <ul
      v-if="
        primaryList &&
        primaryList.productGroups &&
        primaryList.productGroups.length > 0
      "
      class="category-grid"
      data-layout="4"
    >
      <template v-for="(productGroup, index) in primaryList.productGroups">
        <template v-if="productGroup.products && productGroup.products[0]">
          <li
            :key="`${productGroup.key}-${productGroup.products[0].key}`"
            class="category-grid__item"
          >
            <ProductTeaser
              :product-group="productGroup"
              :product="productGroup.products[0]"
              :is-user-authenticated="isUserAuthenticated"
              :hideFavoriteForm="hideFavorites"
              :use-gtm-tracking="useProductGtmTracking"
              :user-id="userId"
              :store="store"
            />
          </li>
          <!-- Render categoryBlocks on the corresponding rowIndex -->
          <template
            v-for="categoryBlock in props.categoryBlocks"
            :key="categoryBlock"
          >
            <template v-if="categoryBlock.rowIndex === index">
              <!-- Render your category block component here -->
              <CategoryBlock :category-block="categoryBlock" />
            </template>
          </template>
        </template>
      </template>
      <!-- Load more -->
      <li
        v-if="thereAreMorePages"
        class="category-grid__item category-grid__item--loadmore pageNum-container"
      >
        <button class="btn btn--loadmore" @click="clickNextPage()">
          {{ $t("loadMore") }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import Facets from "../../components/search/facets.vue";
import Pills from "../../components/search/pills.vue";
import ProductTeaser from "../../components/productTeaser/productTeaser.vue";
import Icon from "../../components/icon/icon.vue";
import { PrimaryList } from "../../services/models/search.models";
import { SubCategories } from "../categoryPage/categoryPage.vue";
import CategoryBlock, {
  CategoryBlockProps,
} from "../categoryBlock/categoryBlock.vue";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import { useProductFilterStore } from "../../stores/productFilterStore";

const props = defineProps<{
  heading?: string;
  seoInformation?: string;
  headingAlreadyOnPage?: boolean;
  subcategories?: SubCategories;
  searchpage: boolean;
  primaryList: PrimaryList | null;
  hideFavorites: boolean;
  isUserAuthenticated: boolean;
  thereAreMorePages: boolean;
  useProductGtmTracking: boolean;
  userId: string;
  categoryBlocks?: Array<CategoryBlockProps>;
  store: string;
}>();

const filterPanel = ref<HTMLElement | null>(null); // Reference to the filter navigation element
const filterButton = ref<HTMLElement | null>(null); // Reference to the filter button element
const isExpanded = ref(false); // Tracks whether the full text is shown
const maxCharacters = 80; // Character limit for SEO truncation
const isSmallScreen = ref(false); // Tracks if the screen size is small
const isSortDropdownVisible = ref(false); // Tracks dropdown visibility
const route = useRoute();
const selectedSortValue = ref(""); // Tracks the selected sort value
const selectedSortLabel = ref("Sortering"); // Tracks the selected sort option
const productFilterStore = useProductFilterStore(); 
const selectedFiltersCountText = computed(() => {
  const count = productFilterStore.selectedFiltersCount;
  return count > 0 ? `(${count})` : ""; // Return "(n)" if filters are active, otherwise an empty string
});

function toggleSortDropdown() {
  isSortDropdownVisible.value = !isSortDropdownVisible.value;
}

function selectSortOption(optionId: string, optionLabel: string) {
  selectedSortValue.value = optionId; // Update the selected sort value
  selectedSortLabel.value = optionLabel; // Update the selected sort label
  isSortDropdownVisible.value = false; // Close the dropdown
  onSortChange(optionId); // Trigger the sort change logic
}

function onSortChange(selected: string) {
  updateSort(selected); // Emit the selected sort option to the parent
  useFilter(); // Apply the filter logic
}

const handleClickOutside = (event) => {
  const clickedOutsidePanel = filterPanel.value && !filterPanel.value.contains(event.target)
  const clickedOutsideButton = filterButton.value && !filterButton.value.contains(event.target)

  if (productFilterStore.showFacets && clickedOutsidePanel && clickedOutsideButton) {
    // Prevent the click from activating underlying elements (like links or buttons)
    event.stopPropagation()
    event.preventDefault()
    event.stopImmediatePropagation() // Stops non-Vue event handlers too

    productFilterStore.showFacets = false
  }
}

const headingLevelWithDefault = computed(() =>
  props.headingAlreadyOnPage ? "h2" : "h1",
);

const truncatedText = computed(() =>
  props.seoInformation && props.seoInformation.length > maxCharacters
    ? `${props.seoInformation.slice(0, maxCharacters)}...`
    : props.seoInformation,
);

onMounted(() => {
  const sortParam = route.query.sort;
  productFilterStore.totalHits = props.primaryList?.totalHits || 0;

  if (sortParam) {
    const foundOption = props.primaryList?.sort.options.find(option => option.id === sortParam);
    if (foundOption) {
      selectedSortValue.value = foundOption.id;
      selectedSortLabel.value = foundOption.label;
    }
  }

  const focusedElement = document.getElementsByClassName("is-active")[0];
  (focusedElement as HTMLAnchorElement | null)?.focus();
  
  const firstSubCategoryElement = document.getElementsByClassName("product-subcategories")[0];
  if (firstSubCategoryElement) {
    firstSubCategoryElement.scrollLeft = 0; // Reset scroll position for subcategories
  }
  handleResize();

  // Add event listeners
  window.addEventListener("resize", handleResize);
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  // Cleanup event listeners
  window.removeEventListener("resize", handleResize);
  document.removeEventListener('click', handleClickOutside, true);
});

// Watch for changes in the showFacets state and scroll to the appropriate element
watch(
  () => productFilterStore.showFacets, 
  (newValue) => {
    if (newValue) {
      const navigationElement = document.querySelector(".product-filter__facet-wrapper");
      if (navigationElement) {
        navigationElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      const navigationElement = document.querySelector(".layout-category__products");
      if (navigationElement) {
        navigationElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }
);

watch(isSmallScreen, () => {
  // Reset the state if the screen size changes
  if (!isSmallScreen.value) {
    isExpanded.value = false;
  }
});

watch(() => route.query.sort, (newSort) => {
  if (newSort) {
    productFilterStore.totalHits = props.primaryList?.totalHits || 0;
    const foundOption = props.primaryList?.sort.options.find(option => option.id === newSort);
    if (foundOption) {
      selectedSortValue.value = foundOption.id;
      selectedSortLabel.value = foundOption.label;
    }
  } else {
    selectedSortValue.value = "";
    selectedSortLabel.value = "Sortering";
  }
});

const emit = defineEmits([
  "upsertFacet",
  "updateSort",
  "useFilter",
  "nextPage",
]);

// Check if the screen size matches the "small" breakpoint
function handleResize() {
  const smallScreenMediaQuery = window.matchMedia("(max-width: 1025px)");
  isSmallScreen.value = smallScreenMediaQuery.matches;
}
/**
 * Toggle read more
 */
function toggleReadMore() {
  isExpanded.value = !isExpanded.value;
}
/**
 * Upsert facet
 */
function upsertFacet(payload) {
  emit("upsertFacet", payload);
}
/**
 * Update sort
 */
function updateSort(payload) {
  emit("updateSort", payload);
}
/**
 * Use filter
 */
function useFilter() {
  productFilterStore.showFacets = false;

  emit("useFilter");
}
/**
 * Click next page
 */
function clickNextPage() {
  emit("nextPage");
}
/**
 * Esc
 */
function esc() {
  productFilterStore.showFacets = false;
}
</script>